import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CallToAction from './CallToAction';
import { Link } from 'react-router-dom';
import { Quote } from 'lucide-react';
import { NeuralGridMock, PlayerUiMock, BrainwaveTableMock, HabitGridMock } from './mocks/BlogMocks';

interface MarkdownRendererProps {
  content: string;
}

const extractText = (children: React.ReactNode): string => {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(extractText).join('');
  }
  if (React.isValidElement(children)) {
    return extractText((children.props as { children?: React.ReactNode }).children);
  }
  return '';
};

const slugifyHeading = (children: React.ReactNode): string | undefined => {
  const text = extractText(children)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return text || undefined;
};

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  // Split content by [CTA] placeholder
  const parts = content.split(/\[CTA\]/g);

  const components: any = {
    h2: ({ node, ...props }: any) => (
      <h2 className="text-3xl font-bold mt-12 mb-6 text-foreground scroll-mt-24 tracking-tight" id={slugifyHeading(props.children)}>
        {props.children}
      </h2>
    ),
    h3: ({ node, ...props }: any) => (
      <h3 className="text-xl font-bold mt-8 mb-4 text-foreground scroll-mt-24" id={slugifyHeading(props.children)}>
        {props.children}
      </h3>
    ),
    p: ({ node, ...props }: any) => {
       // If a paragraph contains only an image, don't wrap it in p tags to avoid nesting issues
       if (node?.children[0]?.tagName === 'figure') return <>{props.children}</>;
       return <p className="text-lg leading-8 text-muted-foreground mb-6" {...props} />;
    },
    ul: ({ node, ...props }: any) => <ul className="my-6 space-y-2 pl-6 list-disc marker:text-primary" {...props} />,
    ol: ({ node, ...props }: any) => (
      <ol className="my-6 space-y-3 list-decimal pl-6 text-lg text-muted-foreground marker:font-bold marker:text-foreground" {...props} />
    ),
    li: ({ node, ...props }: any) => <li className="text-lg text-muted-foreground leading-relaxed pl-2" {...props} />,
    
    blockquote: ({ node, ...props }: any) => (
      <div className="my-10 relative pl-8 pr-4 py-2 border-l-4 border-primary/30 bg-muted/30 rounded-r-xl">
        <Quote className="absolute top-2 left-2 text-primary/20 w-8 h-8 -z-10" />
        <blockquote className="text-xl font-medium italic text-foreground/80 leading-relaxed" {...props} />
      </div>
    ),
    img: ({ node, ...props }: any) => (
      <figure className="my-10 rounded-xl overflow-hidden shadow-2xl border border-border bg-muted">
        <img {...props} className="w-full h-auto object-cover" loading="lazy" />
        {props.alt && <figcaption className="p-3 text-center text-sm text-muted-foreground bg-card/50 backdrop-blur-sm border-t border-border/50">{props.alt}</figcaption>}
      </figure>
    ),
    a: ({ node, ...props }: any) => {
      const isInternal = props.href && (props.href.startsWith('/') && !props.href.startsWith('//'));
      const classes = "text-primary font-semibold hover:underline decoration-2 underline-offset-2 transition-colors";
      if (isInternal) {
        return <Link to={props.href} className={classes} {...props} />;
      }
      return <a target="_blank" rel="noopener noreferrer" className={classes} {...props} />;
    },
    code: ({ node, inline, className, children, ...props }: any) => {
      const match = /language-(\w+)/.exec(className || '');
      const lang = match ? match[1] : '';

      // Custom Mock Rendering logic
      if (!inline && lang === 'mock') {
        const mockName = String(children).replace(/\n$/, '').trim();
        switch (mockName) {
            case 'NeuralGrid': return <NeuralGridMock />;
            case 'PlayerUi': return <PlayerUiMock />;
            case 'BrainwaveTable': return <BrainwaveTableMock />;
            case 'HabitGrid': return <HabitGridMock />;
            default: return <div className="p-4 border border-dashed border-red-300 text-red-500 rounded">Mock "{mockName}" not found</div>;
        }
      }

      if (inline) {
        return <code className="bg-muted text-primary font-mono text-sm px-1.5 py-0.5 rounded border border-border" {...props}>{children}</code>;
      }
      
      return (
        <pre className="bg-slate-950 text-slate-50 p-4 rounded-xl overflow-x-auto my-6 border border-slate-800 shadow-inner">
          <code className={className} {...props}>{children}</code>
        </pre>
      );
    }
  };

  return (
    <div className="prose-container max-w-none">
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
            {part}
          </ReactMarkdown>
          {/* Render CTA between parts, but not after the last part */}
          {index < parts.length - 1 && <CallToAction />}
        </React.Fragment>
      ))}
    </div>
  );
};

export default MarkdownRenderer;
